import Vue from 'vue';
import select from 'dom-select';
import { isMobile } from 'utils/general';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { i18n } from 'lib/i18n.js';
import on, { off } from 'dom-event';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

const YOUTUBE_URL_REGEX =
  /(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/;
const MAX_VIDEOS_PER_PAGE = 15;

function replaceURLById(urlValue) {
  const match = urlValue.match(YOUTUBE_URL_REGEX);
  if (match && match[1]) {
    return match[1];
  }
  return urlValue;
}

export default (el) =>
  new Vue({
    components: {
      'vue-image': () => import('../vue-image/vue-image.vue'),
      'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
    },
    el,
    i18n,
    data: () => ({
      videos: [],
      isMobile: isMobile(),
      videosInPage: MAX_VIDEOS_PER_PAGE,
      showModal: false,
      videoType: 'grid',
      videoIndex: 0,
      perPage: MAX_VIDEOS_PER_PAGE,
    }),
    mounted() {
      this.initVideos();
      on(window, 'resize', debounce(this.resizeHandler, 300));
    },
    destroyed() {
      off(window, 'resize', this.resizeHandler);
    },
    computed: {
      heroVideos() {
        return this.videos.filter((v) => v.hero === true);
      },
      gridVideos() {
        return this.videos.filter((v) => v.hero !== true);
      },
      videoId() {
        if (this.videoType === 'grid') {
          return get(this.gridVideos[this.videoIndex], 'video', '');
        }
        return get(this.heroVideos[this.videoIndex], 'video', '');
      },
      videoTitle() {
        if (this.videoType === 'grid') {
          return get(this.gridVideos[this.videoIndex], 'title', '');
        }
        return get(this.heroVideos[this.videoIndex], 'title', '');
      },
      embedSrc() {
        return `https://www.youtube.com/embed/${this.videoId}?rel=1&enablejsapi=1`;
      },
      loadedText() {
        return this.$t('how_to_videos.loaded', {
          start: this.gridVideos.length > 0 ? 1 : 0,
          end: Math.min(this.gridVideos.length, this.videosInPage),
          total: this.gridVideos.length,
        });
      },
    },
    methods: {
      initVideos() {
        const dataTag = select('#how-to-videos-data');
        if (!dataTag) return;

        const rawJson = dataTag.textContent;
        const parsed = JSON.parse(rawJson);
        const arrayValue = [];
        for (let index = 0; index < parsed.video.length; index++) {
          if (parsed.video[index] === '') continue;

          arrayValue.push({
            video: replaceURLById(parsed.video[index]),
            title: parsed.title[index],
            thumbnail: parsed.thumbnail[index],
            brand: parsed.brand[index],
            hero: parsed.hero[index] === 'true',
          });
        }
        this.videos = arrayValue;

        this.perPage = parseInt(
          get(el.dataset, 'perPage', MAX_VIDEOS_PER_PAGE),
        );
        this.videosInPage = this.perPage;
      },
      resizeHandler() {
        this.isMobile = isMobile();
      },
      getVideoThumbnail(video, quality = 'low') {
        // quality -> low, medium, high
        const qualityRange = {
          low: 0,
          medium: 1,
          high: 2,
        };

        const qualityValue = qualityRange[quality];

        if (video.thumbnail.length > 0) {
          return this.getResizedImage(video, qualityValue);
        } else {
          return this.getYoutubeThumbnail(video, qualityValue);
        }
      },
      getYoutubeThumbnail({ video }, quality = 2) {
        const qualityValues = ['mqdefault', 'hqdefault', 'maxresdefault'];

        return `https://img.youtube.com/vi/${video}/${qualityValues[quality]}.jpg`;
      },
      getResizedImage(video, quality = 2) {
        const qualityValues = ['w_400', 'w_800', 'w_1200'];

        if (!video.thumbnail[0]) return false;

        const cloudinaryUrl = video.thumbnail[0].cloudinary_src;

        return `${cloudinaryUrl}${qualityValues[quality]}`;
      },
      openVideo(type, videoIndex) {
        // type -> 'grid' | 'hero'
        this.videoType = type;
        this.videoIndex = videoIndex;

        this.showModal = true;

        this.$nextTick(() => {
          if (this.$refs.videoModal) {
            disableBodyScroll(this.$refs.videoModal);
          }
        });
      },
      closeModal() {
        this.showModal = false;
        clearAllBodyScrollLocks();
      },
      nextVideo() {
        let noOfVideos = 0;
        if (this.videoType === 'grid') {
          noOfVideos = this.gridVideos.length;
        } else {
          noOfVideos = this.heroVideos.length;
        }
        if (this.videoIndex + 1 >= noOfVideos) {
          if (this.videoType === 'grid' && this.heroVideos.length) {
            // if grid rotate to hero (if there are hero videos to play)
            this.videoType = 'hero';
            this.videoIndex = 0;
          } else if (this.videoType === 'hero' && this.gridVideos.length) {
            // if hero rotate to grid (If there are grid videos to play)
            this.videoType = 'grid';
            this.videoIndex = 0;
          } else {
            this.videoIndex = 0;
          }
        } else {
          this.videoIndex += 1;
        }
      },
      previousVideo() {
        if (this.videoIndex <= 0) {
          if (this.videoType === 'grid' && this.heroVideos.length) {
            // if grid rotate to hero (if there are hero videos to play)
            this.videoType = 'hero';
            this.videoIndex = this.heroVideos.length - 1;
          } else if (this.videoType === 'hero' && this.gridVideos.length) {
            // if hero rotate to grid (If there are grid videos to play)
            this.videoType = 'grid';
            this.videoIndex = this.gridVideos.length - 1;
          } else {
            // At this point in the conditional we know that one of the video groups are empty.
            this.videoIndex =
              Math.max(this.gridVideos.length - 1, 0) +
              Math.max(this.heroVideos.length - 1, 0);
          }
        } else {
          this.videoIndex -= 1;
        }
      },
      loadMore() {
        this.videosInPage += this.perPage;
        if (this.videosInPage > this.gridVideos.length) {
          this.videosInPage = this.gridVideos.length;
        }
      },
    },
  });
